import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription } from 'rxjs';
import {
    DEFAULT_ACCEPTED_EXTENSIONS,
    DEFAULT_MAX_FILE_SIZE_BYTES,
    DEFAULT_MAX_TOTAL_SIZE_BYTES,
    DEFAULT_MIN_FILE_SIZE_BYTES,
    FileUploadDialogConfig,
    SubmittedFile,
    UploadEntry,
} from './file-upload-dialog.types';
import { ESPP_FILE, ESPP_FILES, ESPP_FILE_SUBMIT } from '../../api.constants';

interface ActiveEntry extends UploadEntry {
    subscription?: Subscription;
}

@Component({
    selector: 'app-file-upload-dialog',
    templateUrl: './file-upload-dialog.component.html',
    styleUrls: ['./file-upload-dialog.component.scss'],
})
export class FileUploadDialogComponent implements OnDestroy {

    public fileForm = new FormGroup({
        file: new FormControl<File | null>(null, [Validators.required]),
    });
    public errorMessage = new BehaviorSubject<string | null>(null);
    public fileValidationErrors: string[] = [];
    public fileUploadErrors = new Map<number, string[]>();

    // Legacy upload mode state
    private _uploading = false;
    public get uploading(): boolean { return this._uploading; }
    public set uploading(value: boolean) {
        this._uploading = value;
        this.updateDisableClose();
    }
    private fileList: File[] = [];

    // Auto-upload mode state (Claims / Enrollments)
    public uploadEntries: ActiveEntry[] = [];
    private _submitting = false;
    public get submitting(): boolean { return this._submitting; }
    public set submitting(value: boolean) {
        this._submitting = value;
        this.updateDisableClose();
    }
    private submitSubscription?: Subscription;

    public readonly acceptedExtensions: string[];
    public readonly minFileSizeBytes: number;
    public readonly maxFileSizeBytes: number;
    public readonly maxTotalSizeBytes: number;

    constructor(
        private matDialog: MatDialogRef<FileUploadDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: FileUploadDialogConfig,
        private http: HttpClient,
    ) {
        this.acceptedExtensions = data.acceptedExtensions ?? DEFAULT_ACCEPTED_EXTENSIONS;
        this.minFileSizeBytes = data.minFileSizeBytes ?? DEFAULT_MIN_FILE_SIZE_BYTES;
        this.maxFileSizeBytes = data.maxFileSizeBytes ?? DEFAULT_MAX_FILE_SIZE_BYTES;
        this.maxTotalSizeBytes = data.maxTotalSizeBytes ?? DEFAULT_MAX_TOTAL_SIZE_BYTES;
    }

    public get isAutoUploadMode(): boolean {
        return !!(this.data.domain && this.data.fileableType && this.data.fileableId != null);
    }

    public get files(): File[] {
        return this.isAutoUploadMode
            ? this.uploadEntries.map(e => e.file)
            : this.fileList;
    }

    public get acceptedFileTypes(): string { return this.acceptedExtensions.join(','); }
    public get minFileSizeLabel(): string { return `${this.minFileSizeBytes / 1024} KB`; }
    public get maxFileSizeLabel(): string { return `${this.maxFileSizeBytes / (1024 * 1024)} MB`; }
    public get maxTotalSizeLabel(): string { return `${this.maxTotalSizeBytes / (1024 * 1024)} MB`; }
    public get existingTotalBytes(): number { return this.data.existingTotalBytes ?? 0; }
    public get newSelectionBytes(): number {
        return this.isAutoUploadMode
            ? this.uploadEntries.reduce((sum, e) => sum + e.file.size, 0)
            : this.fileList.reduce((sum, f) => sum + f.size, 0);
    }
    public get cumulativeTotalBytes(): number { return this.existingTotalBytes + this.newSelectionBytes; }
    public get isUploadMode(): boolean { return !!this.data.uploadFn; }
    public get existingFileCount(): number { return this.data.existingFileCount ?? 0; }
    public get maxFiles(): number | null { return this.data.maxFiles ?? null; }
    public get dialogTitle(): string { return this.data.title ?? 'Upload Files'; }
    public get dialogDescription(): string { return this.data.description ?? 'Select the file(s) you would like to upload.'; }
    public get footerNote(): string | null { return this.data.footerNote ?? null; }

    public get canSubmit(): boolean {
        return this.uploadEntries.some(e => e.status === 'uploaded')
            && !this.uploadEntries.some(e => e.status === 'uploading' || e.status === 'deleting')
            && !this.submitting;
    }

    /** True while closing the dialog could orphan an in-flight upload/delete or a submission the parent hasn't seen yet. */
    public get isBusy(): boolean {
        if (this.uploading || this.submitting) return true;
        return this.isAutoUploadMode && this.uploadEntries.some(e => e.status === 'uploading' || e.status === 'deleting');
    }

    private updateDisableClose(): void {
        this.matDialog.disableClose = this.isBusy;
    }

    public formatFileSize(bytes: number): string {
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    public getFileUploadErrors(index: number): string[] {
        return this.fileUploadErrors.get(index) ?? [];
    }

    public getEntryStatus(index: number): 'uploading' | 'uploaded' | 'error' | 'deleting' | null {
        return this.uploadEntries[index]?.status ?? null;
    }

    public getEntryUploadError(index: number): string | null {
        return this.uploadEntries[index]?.uploadError ?? null;
    }

    public getEntryDeleteError(index: number): string | null {
        return this.uploadEntries[index]?.deleteError ?? null;
    }

    private isAcceptedFileType(file: File): boolean {
        const name = file.name.toLowerCase();
        return this.acceptedExtensions.some(ext => name.endsWith(ext));
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const selected = input.files ? Array.from(input.files) : [];
        input.value = '';
        this.fileValidationErrors = [];
        if (!this.isAutoUploadMode) {
            this.fileUploadErrors.clear();
            this.errorMessage.next(null);
        }

        const validTypeFiles: File[] = [];
        const invalidFiles: string[] = [];
        selected.forEach(f => {
            if (this.isAcceptedFileType(f)) validTypeFiles.push(f);
            else invalidFiles.push(f.name);
        });
        if (invalidFiles.length) {
            this.fileValidationErrors.push(`Unsupported file type: ${invalidFiles.join(', ')}`);
        }

        const sizeValidFiles: File[] = [];
        const undersizedFiles: string[] = [];
        const oversizedFiles: string[] = [];
        validTypeFiles.forEach(f => {
            if (f.size < this.minFileSizeBytes) undersizedFiles.push(f.name);
            else if (f.size > this.maxFileSizeBytes) oversizedFiles.push(f.name);
            else sizeValidFiles.push(f);
        });
        if (undersizedFiles.length) {
            this.fileValidationErrors.push(`File(s) must be at least ${this.minFileSizeLabel}: ${undersizedFiles.join(', ')}`);
        }
        if (oversizedFiles.length) {
            this.fileValidationErrors.push(`File(s) exceed the ${this.maxFileSizeLabel} limit: ${oversizedFiles.join(', ')}`);
        }

        // Cumulative total: in auto-upload mode, include already-queued entries
        const alreadyAcceptedBytes = this.isAutoUploadMode
            ? this.uploadEntries.reduce((sum, e) => sum + e.file.size, 0)
            : 0;
        const proposedTotal = this.existingTotalBytes + alreadyAcceptedBytes + sizeValidFiles.reduce((sum, f) => sum + f.size, 0);
        if (proposedTotal > this.maxTotalSizeBytes) {
            this.fileValidationErrors.push(`Total attached files cannot exceed ${this.maxTotalSizeLabel}.`);
            if (!this.isAutoUploadMode) {
                this.fileList = [];
                this.fileForm.controls.file.setValue(null);
            }
            return;
        }

        const currentCount = this.isAutoUploadMode ? this.uploadEntries.length : 0;
        if (this.maxFiles !== null && this.existingFileCount + currentCount + sizeValidFiles.length > this.maxFiles) {
            const remaining = Math.max(0, this.maxFiles - this.existingFileCount - currentCount);
            this.fileValidationErrors.push(
                remaining === 0
                    ? `Maximum of ${this.maxFiles} file(s) already attached.`
                    : `You can attach ${remaining} more file(s) (limit: ${this.maxFiles} total).`
            );
            if (!this.isAutoUploadMode) {
                this.fileList = [];
                this.fileForm.controls.file.setValue(null);
            }
            return;
        }

        if (sizeValidFiles.length === 0) {
            if (!this.isAutoUploadMode) this.fileForm.controls.file.setValue(null);
            return;
        }

        if (this.isAutoUploadMode) {
            sizeValidFiles.forEach(file => {
                const entry: ActiveEntry = { file, status: 'uploading' };
                this.uploadEntries.push(entry);
                this.startFileUpload(entry);
            });
            this.updateDisableClose();
        } else {
            this.fileList = sizeValidFiles;
            this.fileForm.controls.file.setValue(this.fileList[0], { emitModelToViewChange: false });
        }
    }

    private startFileUpload(entry: ActiveEntry): void {
        const formData = new FormData();
        formData.append('module', this.data.domain!);
        formData.append('file_category', 'GENERAL');
        formData.append('files[]', entry.file);

        const sub = this.http.post<any>(ESPP_FILES, formData).subscribe({
            next: (response) => {
                const fileData = Array.isArray(response) ? response[0] : response;
                const index = this.uploadEntries.indexOf(entry);
                if (index !== -1) {
                    this.uploadEntries[index] = {
                        file: entry.file,
                        status: 'uploaded',
                        fileId: fileData?.id,
                        url: fileData?.url,
                    };
                }
                this.updateDisableClose();
            },
            error: (err) => {
                const index = this.uploadEntries.indexOf(entry);
                if (index !== -1) {
                    this.uploadEntries[index] = {
                        file: entry.file,
                        status: 'error',
                        uploadError: err?.error?.message ?? 'Upload failed. Please try again.',
                    };
                }
                this.updateDisableClose();
            },
        });
        entry.subscription = sub;
    }

    public removeSelectedFile(index: number): void {
        if (this.isAutoUploadMode) {
            const entry = this.uploadEntries[index];
            if (!entry || entry.status === 'deleting') return;

            if (entry.fileId == null) {
                entry.subscription?.unsubscribe();
                this.uploadEntries.splice(index, 1);
                this.updateDisableClose();
                return;
            }

            const previousStatus = entry.status;
            entry.status = 'deleting';
            entry.deleteError = undefined;
            this.updateDisableClose();

            this.http.delete(ESPP_FILE(entry.fileId)).subscribe({
                next: () => {
                    const currentIndex = this.uploadEntries.indexOf(entry);
                    if (currentIndex !== -1) this.uploadEntries.splice(currentIndex, 1);
                    this.updateDisableClose();
                },
                error: (err) => {
                    entry.status = previousStatus;
                    entry.deleteError = err?.error?.message ?? 'Unable to remove file. Please try again.';
                    this.updateDisableClose();
                },
            });
            return;
        }

        this.fileList.splice(index, 1);
        this.fileUploadErrors.clear();
        this.errorMessage.next(null);
        if (this.fileList.length === 0) this.fileForm.controls.file.setValue(null);
    }

    public confirm(): void {
        if (this.isAutoUploadMode) {
            if (!this.canSubmit) return;
            const fileIds = this.uploadEntries
                .filter(e => e.status === 'uploaded' && e.fileId != null)
                .map(e => e.fileId!);

            this.submitting = true;
            this.errorMessage.next(null);
            this.submitSubscription = this.http.post(
                ESPP_FILE_SUBMIT(this.data.fileableType!, this.data.fileableId!),
                { file_id: fileIds },
            ).subscribe({
                next: () => {
                    this.submitting = false;
                    const submittedFiles: SubmittedFile[] = this.uploadEntries
                        .filter((e): e is ActiveEntry & { fileId: number } => e.status === 'uploaded' && e.fileId != null)
                        .map(e => ({
                            id: e.fileId,
                            name: e.file.name,
                            size: e.file.size,
                            url: e.url ?? '',
                            category: 'GENERAL',
                        }));
                    this.matDialog.close(submittedFiles);
                },
                error: (err) => {
                    this.submitting = false;
                    this.errorMessage.next(err?.error?.message ?? 'Submission failed. Please try again.');
                },
            });
            return;
        }

        if (this.fileForm.invalid || this.fileList.length === 0 || this.fileValidationErrors.length > 0) return;

        const uploadFn = this.data.uploadFn;
        if (!uploadFn) {
            this.matDialog.close([...this.fileList]);
            return;
        }

        this.uploading = true;
        this.fileUploadErrors.clear();
        this.errorMessage.next(null);

        let pending = this.fileList.length;
        let hasError = false;

        this.fileList.forEach((file, index) => {
            uploadFn(file).subscribe({
                error: (err) => {
                    hasError = true;
                    pending--;
                    this.uploading = pending > 0;

                    if (err?.error?.errors) {
                        const msgs: string[] = Object.values(err.error.errors).flat() as string[];
                        this.fileUploadErrors.set(index, msgs.map(m => m.replace(/files\.\d+/g, file.name)));
                    }
                    if (err?.error?.message) {
                        this.errorMessage.next(err.error.message);
                    } else if (!this.fileUploadErrors.size) {
                        this.errorMessage.next('An error occurred while uploading. Please try again.');
                    }
                },
                complete: () => {
                    pending--;
                    if (pending === 0 && !hasError) {
                        this.uploading = false;
                        this.matDialog.close();
                    }
                },
            });
        });
    }

    /**
     * Invoked by the close (X) and Cancel controls. Deletes any files that were already
     * uploaded to the server but never submitted, so canceling never leaves an orphaned
     * file behind. A no-op while a request is in flight — those controls are disabled
     * via `isBusy`/`disableClose`, but this guards against programmatic invocation too.
     */
    public closeDialog(): void {
        if (this.isBusy) return;
        this.cleanupUnsubmittedFiles();
        this.matDialog.close();
    }

    private cleanupUnsubmittedFiles(): void {
        if (!this.isAutoUploadMode) return;
        this.uploadEntries
            .filter((e): e is ActiveEntry & { fileId: number } => e.status === 'uploaded' && e.fileId != null)
            .forEach(e => this.http.delete(ESPP_FILE(e.fileId)).subscribe());
    }

    ngOnDestroy(): void {
        this.uploadEntries.forEach(e => e.subscription?.unsubscribe());
        this.submitSubscription?.unsubscribe();
    }
}
